import type { AuthTokens } from '@/lib/types/auth';

/**
 * Wire format for an E2E-seeded session: `base64url(JSON.stringify(AuthTokens))`, passed to
 * `app/e2e/write-token.tsx` as the `?data=` deep-link param on web.
 *
 * This codec is the **single source of truth** for that shape. The web-side minter
 * `.maestro/scripts/mint-token.mjs` runs in a separate (Node) runtime and can't import this
 * module, so it MUST emit exactly {@link encodeAuthTokens}'s output — `auth-token-codec.unit.test.ts`
 * pins the format (a golden blob) so the two sides can't silently drift. `write-token.tsx`
 * decodes with {@link decodeAuthTokens}.
 *
 * `AuthTokens` values are ASCII (base64url JWTs + numbers), so `btoa`/`atob` (available in the
 * Expo/Hermes runtime and in Node ≥18) are sufficient — no `node-forge`/`Buffer` needed.
 */
export function encodeAuthTokens(tokens: AuthTokens): string {
  return btoa(JSON.stringify(tokens))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

export function decodeAuthTokens(data: string): AuthTokens {
  const b64 = data.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
  return JSON.parse(atob(padded)) as AuthTokens;
}
